/**
 * PlayShop — Mode collection SEALED (binary owned/not).
 *
 * Sur toutes les pages "produit scellé" : /collections/pokemon-elite-trainer-box,
 * /collections/pokemon-display (futur : /collections/pokemon-boosters, etc.) et
 * fiches produit ETB / Display :
 *   - Bouton étoile burger (pos-4) toggle le mode collection (body.is-collection-mode)
 *   - En mode collection : le clic sur les boutons variants (quick-add cart) appelle
 *     l'Edge function `sealed-collection-toggle` au lieu d'ajouter au panier
 *   - Les variants possédées ont la classe .is-owned (checkmark visuel)
 *   - Sur la fiche scellée : bouton dédié "Ajouter à ma collection" (data-ps-sealed-collect-btn)
 *   - Persisté via localStorage (par user)
 *
 * Prérequis HTML (génériques pour tout produit scellé) :
 *   - bouton [data-sealed-toggle-collection] (collection page) avec data-customer-id / data-licence-slug
 *   - OU bouton [data-ps-sealed-collect-btn] (fiche produit) avec data-variant-id / data-customer-id
 *
 * ⚠ Pattern verrouillé 2026-06-03 : les boutons HTML `disabled` (OOS via Liquid)
 * n'émettent AUCUN click event, même avec pointer-events:auto en CSS.
 * En activant le mode collection on retire `disabled` (en stockant l'état initial
 * dans data-ps-was-disabled) et on restaure en quittant.
 *
 * Backend :
 *   - Supabase edge function `sealed-collection-toggle` (générique tout product_type)
 *   - Lookup dans table `sealed_catalog` (sync via sync-sealed-catalog.mjs)
 *   - Écriture dans `collection_cards` avec item_type='sealed' + variant_type='sealed-{etb|display|booster|…}'
 */
(function () {
  'use strict';

  const SUPABASE_FN_BASE = 'https://ipvzpgwgujamtzztxvcu.supabase.co/functions/v1';
  const STORAGE_KEY = 'ps-sealed-collection-mode';

  // Bouton étoile burger sealed (page collection) OU bouton inline (fiche produit scellé).
  // Un des deux suffit pour activer le JS. Sur la fiche produit, le snippet tools
  // n'est rendu que sur template.collection, donc seul l'inlineBtn existe.
  // Compat legacy : on accepte aussi les anciens attributs [data-etb-*] le temps de la transition.
  const toggleBtn = document.querySelector('[data-sealed-toggle-collection], [data-etb-toggle-collection]');
  const inlineBtn = document.querySelector('[data-ps-sealed-collect-btn], [data-ps-etb-collect-btn]');
  if (!toggleBtn && !inlineBtn) return; // pas sur une page scellée → exit silencieux

  // Détermine customerId / licenceSlug depuis le toggle (préféré) ou le bouton inline.
  let customerId = '';
  let licenceSlug = 'pokemon';
  if (toggleBtn) {
    customerId = toggleBtn.dataset.customerId || '';
    licenceSlug = toggleBtn.dataset.licenceSlug || 'pokemon';
  } else if (inlineBtn) {
    customerId = inlineBtn.dataset.customerId || '';
    licenceSlug = inlineBtn.dataset.licenceSlug || 'pokemon';
  }
  const isLoggedIn = customerId !== '' && customerId !== '0';

  // Cache local des variants ETB possédées par le user (anti-flash + offline)
  const CACHE_KEY = `ps-etb-owned-${customerId}-${licenceSlug}`;
  const ownedVariantIds = new Set();
  if (isLoggedIn) {
    try {
      const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || '[]');
      cached.forEach((id) => ownedVariantIds.add(Number(id)));
    } catch (e) {}
  }
  function persistOwnedCache() {
    if (!isLoggedIn) return;
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(Array.from(ownedVariantIds)));
    } catch (e) {}
  }

  const body = document.body;

  // ─── Toast (uniquement pour les erreurs) ─────
  let toastEl = null, toastTimer = null;
  function showToast(msg) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'ps-tcghub-toast';
      toastEl.setAttribute('role', 'status');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = msg;
    clearTimeout(toastTimer);
    requestAnimationFrame(() => toastEl.classList.add('is-visible'));
    toastTimer = setTimeout(() => toastEl.classList.remove('is-visible'), 3500);
  }

  // ─── Détection : un form de card appartient-il à un produit scellé ? ─────
  // Sur les pages collection scellée (pokemon-elite-trainer-box, pokemon-display, etc.),
  // TOUTES les cards sont des produits scellés → on n'a pas besoin de filtrer par
  // product.type (qui peut varier dans le rendu HTML escape). Si le toggle burger
  // sealed existe, on accepte tous les forms de la page.
  function isSealedForm(form) {
    if (toggleBtn) return true; // collection scellée → tout est sealed
    const card = form.closest('.ps-card');
    if (!card) return false;
    const type = (card.dataset.psProductType || '').toUpperCase();
    return type === 'ETB' || type === 'DISPLAY' || type === 'BOOSTER';
  }

  // ─── Update visuel des boutons variants selon owned state ─────
  function refreshOwnedVisuals() {
    const cardForms = toggleBtn
      ? document.querySelectorAll('.ps-card__variant-form')
      : document.querySelectorAll(
          '.ps-card[data-ps-product-type="ETB"] .ps-card__variant-form, ' +
          '.ps-card[data-ps-product-type="Display"] .ps-card__variant-form, ' +
          '.ps-card[data-ps-product-type="Booster"] .ps-card__variant-form'
        );
    cardForms.forEach((form) => {
      const idInput = form.querySelector('input[name="id"]');
      if (!idInput) return;
      const variantId = Number(idInput.value);
      const btn = form.querySelector('button');
      if (!btn) return;
      btn.classList.toggle('is-owned', ownedVariantIds.has(variantId));
    });
    // Bouton dédié fiche produit scellé (sealed OU legacy etb)
    document.querySelectorAll('[data-ps-sealed-collect-btn], [data-ps-etb-collect-btn]').forEach((btn) => {
      const variantId = Number(btn.dataset.variantId || 0);
      if (!variantId) return;
      btn.classList.toggle('is-owned', ownedVariantIds.has(variantId));
      const label = btn.querySelector('[data-ps-sealed-collect-label], [data-ps-etb-collect-label]');
      if (label) {
        label.textContent = ownedVariantIds.has(variantId)
          ? 'Dans ma collection ✓'
          : 'Ajouter à ma collection';
      }
    });
  }

  // ─── Apply mode global ─────
  // ⚠ Bouton disabled ≠ clickable : en mode collection on retire l'attribut HTML
  // `disabled` (et `aria-disabled`) des cards ETB (souvent OOS). Stocké dans
  // data-ps-was-disabled pour pouvoir restaurer en sortie.
  function applyMode(active) {
    if (active) {
      body.classList.add('is-collection-mode');
      document
        .querySelectorAll('.ps-card__variant-form button[disabled], .ps-card__form button.ps-card__cta[disabled]')
        .forEach((btn) => {
          btn.setAttribute('data-ps-was-disabled', 'true');
          btn.removeAttribute('disabled');
          btn.removeAttribute('aria-disabled');
        });
    } else {
      body.classList.remove('is-collection-mode');
      document.querySelectorAll('button[data-ps-was-disabled="true"]').forEach((btn) => {
        btn.setAttribute('disabled', '');
        btn.setAttribute('aria-disabled', 'true');
        btn.removeAttribute('data-ps-was-disabled');
      });
    }
    if (toggleBtn) {
      toggleBtn.setAttribute('aria-pressed', String(active));
      toggleBtn.classList.toggle('is-active', active);
    }
  }

  // ─── Toggle owned via edge function ─────
  async function toggleOwned(variantId) {
    if (!isLoggedIn) {
      showToast('Connectez-vous pour suivre votre collection.');
      return;
    }
    const wasOwned = ownedVariantIds.has(variantId);
    // Optimistic update
    if (wasOwned) ownedVariantIds.delete(variantId);
    else ownedVariantIds.add(variantId);
    refreshOwnedVisuals();
    persistOwnedCache();

    try {
      const r = await fetch(`${SUPABASE_FN_BASE}/sealed-collection-toggle`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // ⚠️ Ce commentaire disait « anon JWT requis » : la clé anon est PUBLIQUE (elle est
          // juste en dessous, en clair, dans un fichier servi à tout le monde) et `verify_jwt`
          // ne prouve donc RIEN sur l'identité — il vérifie qu'on présente UNE clé, pas QUI on
          // est. Prouvé en live : une fn verify_jwt:true rendait la collection privée d'autrui.
          // La seule preuve d'identité est `x-ps-token`, propre au client.
          apikey: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImlwdnpwZ3dndWphbXR6enR4dmN1Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3Nzg2OTI0NDgsImV4cCI6MjA5NDI2ODQ0OH0.DSIqV61WBxK_HfT7mW3fctN4JcV-kofkILlxVu9EOuI',
          Authorization: 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImlwdnpwZ3dndWphbXR6enR4dmN1Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3Nzg2OTI0NDgsImV4cCI6MjA5NDI2ODQ0OH0.DSIqV61WBxK_HfT7mW3fctN4JcV-kofkILlxVu9EOuI',
          'x-ps-token': window.PS_AUTH_TOKEN || '',
        },
        body: JSON.stringify({
          shopify_customer_id: Number(customerId),
          shopify_variant_id: variantId,
        }),
      });
      const data = await r.json();
      if (data.error) {
        // Rollback
        if (wasOwned) ownedVariantIds.add(variantId);
        else ownedVariantIds.delete(variantId);
        refreshOwnedVisuals();
        persistOwnedCache();
        showToast(
          data.error === 'sealed not in catalog (sync needed?)'
            ? 'Coffret pas encore synchronisé - réessaie plus tard.'
            : 'Erreur, réessaie.'
        );
        return;
      }
      // Sync state avec server response (pas de toast success — feedback visuel via bouton vert)
      if (data.owned) ownedVariantIds.add(variantId);
      else ownedVariantIds.delete(variantId);
      refreshOwnedVisuals();
      persistOwnedCache();
    } catch (e) {
      // Rollback
      if (wasOwned) ownedVariantIds.add(variantId);
      else ownedVariantIds.delete(variantId);
      refreshOwnedVisuals();
      persistOwnedCache();
      showToast('Connexion impossible, réessaie.');
    }
  }

  // ─── Init : restore mode au reload si user connecté ─────
  let initialActive = false;
  if (isLoggedIn) {
    try { initialActive = localStorage.getItem(STORAGE_KEY) === 'true'; } catch (e) {}
  }
  applyMode(initialActive);

  // Apply visuels SYNC depuis cache (anti-flash)
  if (isLoggedIn && ownedVariantIds.size > 0) {
    requestAnimationFrame(refreshOwnedVisuals);
  }

  // ─── Click handler de l'étoile burger (seulement si présente) ─────
  if (toggleBtn) {
    toggleBtn.addEventListener('click', () => {
      if (!isLoggedIn) {
        showToast('Connectez-vous pour activer le mode collection.');
        return;
      }
      const willBeActive = !body.classList.contains('is-collection-mode');
      applyMode(willBeActive);
      try { localStorage.setItem(STORAGE_KEY, String(willBeActive)); } catch (e) {}
    });
  }

  // ─── Intercept clicks sur les boutons variants en mode collection ─────
  // Capture phase pour devancer le handler du cart drawer.
  document.addEventListener('click', (e) => {
    if (!body.classList.contains('is-collection-mode')) return;
    const btn = e.target.closest('.ps-card__variant-form button, .ps-card__form button.ps-card__cta');
    if (!btn) return;
    const form = btn.closest('form');
    if (!form) return;
    if (!isSealedForm(form)) return; // laisse playshop-tcg-collection-mode.js gérer les TCG
    const idInput = form.querySelector('input[name="id"]');
    if (!idInput) return;
    e.preventDefault();
    e.stopPropagation();
    const variantId = Number(idInput.value);
    if (variantId) toggleOwned(variantId);
  }, true);

  // ─── Click handler bouton dédié "Ajouter à ma collection" sur fiche scellée ─────
  // Hors mode collection : le bouton fonctionne TOUJOURS (toggle direct).
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-ps-sealed-collect-btn], [data-ps-etb-collect-btn]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    const variantId = Number(btn.dataset.variantId || 0);
    if (variantId) toggleOwned(variantId);
  });
})();
